import { bool, equal, readOnly } from '@ember/object/computed';
import { computed } from '@ember/object';
import { isNone } from '@ember/utils';
import Model from '@ember-data/model';
import attr from 'ember-data/attr';
import { belongsTo } from 'ember-data/relationships';
import SubjectModel from 'cio/models/subject';
import ActionModel from 'cio/models/action';
import DeliveryModel from 'cio/models/delivery';
import { SubjectStepExplanation, SubjectStepStatus } from 'cio/utils/subjects';

const DELAY_TYPES = ['skipped_delay', 'skip_delay', 'scheduled'] as const;
const NAMED_TYPES = [
  'email',
  'webhook',
  'push',
  'twilio',
  'slack',
  'urban_airship',
  'in_app',
  'attribute_update',
  'static_seg_update',
  'skipped_delivery',
  'create_event',
] as const;
const DELIVERY_TYPES = ['email', 'webhook', 'push', 'twilio', 'slack', 'urban_airship', 'in_app'] as const;
const SKIPPED_TYPES = ['skipped_delay', 'skipped_delivery'] as const;

const OTHER_SUBJECT_STEP_TYPES = [
  'filter_match_delay',
  'match_campaign',
  'hold',
  'attribute_update',
  'query_collection',
  'static_seg_update',
  'exit_campaign',
  'skeleton',
  'grace_period',
  'conditional_branch',
  'multi_split_branch',
  'multi_lang',
  'random_cohort_branch',
] as const;

type TYPE_STRING_TYPE =
  | (typeof DELAY_TYPES)[number]
  | (typeof NAMED_TYPES)[number]
  | (typeof DELIVERY_TYPES)[number]
  | (typeof SKIPPED_TYPES)[number]
  | (typeof OTHER_SUBJECT_STEP_TYPES)[number];

export default class SubjectStepModel extends Model {
  @attr('string') type!: TYPE_STRING_TYPE;

  // Timestamps
  @attr('number') entered!: number;
  @attr('number') due!: number;
  @attr('number') exited!: number;

  // Other metadata
  @attr('number') iteration!: number;
  @attr('boolean') tracked!: boolean;
  @attr('boolean') draft!: boolean;
  @attr('boolean', { defaultValue: false }) future!: boolean;
  @attr('number') splitIndex!: number;
  @attr('number') branchIndex!: number;
  @attr('string') deliveryId!: string;

  @attr('camel-case') status?: SubjectStepStatus;

  @attr('camel-case') explanations!: SubjectStepExplanation[];

  @belongsTo('subject', { async: false, inverse: 'subjectSteps' }) subject!: SubjectModel;
  @belongsTo('action', { async: false, inverse: null }) action!: ActionModel;
  @belongsTo('delivery', { async: true, inverse: null }) delivery!: DeliveryModel;

  @computed('action.isSplitTest', 'splitIndex')
  get splitAction() {
    if (!this.action?.isSplitTest) {
      // eslint-disable-next-line getter-return
      return;
    }

    let index = this.splitIndex;

    if (index <= 0) {
      // eslint-disable-next-line getter-return
      return;
    }

    return this.action.splitActions[index - 1];
  }

  @computed('due', 'future', 'exited', 'type')
  get current() {
    if (this.future) {
      return false;
    }

    return !isNone(this.due) || (!this.exited && this.type === 'hold');
  }

  @bool('deliveryId') hasDelivery!: boolean;

  @equal('type', 'match_campaign') isMatch!: boolean;
  @equal('type', 'hold') isHold!: boolean;
  @computedTypeIn(DELAY_TYPES) isDelay!: boolean;
  @equal('type', 'attribute_update') isAttributeUpdate!: boolean;
  @equal('type', 'query_collection') isCollectionQuery!: boolean;
  @equal('type', 'static_seg_update') isManualSegmentUpdate!: boolean;
  @computedTypeIn(NAMED_TYPES) isNamed!: boolean;
  @computedTypeIn(SKIPPED_TYPES) isSkipped!: boolean;
  @readOnly('action.isSplitTest') isSplitTest!: boolean;
  @computedTypeIn(DELIVERY_TYPES) isDelivery!: boolean;
  @equal('type', 'exit_campaign') isExit!: boolean;
  @equal('type', 'skeleton') isSkeleton!: boolean;
  @equal('type', 'grace_period') isGracePeriod!: boolean;
  @equal('type', 'conditional_branch') isConditionalBranch!: boolean;
  @equal('type', 'multi_split_branch') isMultiSplitBranch!: boolean;
  @equal('type', 'multi_lang') isMultiLangBranch!: boolean;
  @equal('type', 'random_cohort_branch') isRandomCohortBranch!: boolean;
  @equal('type', 'create_event') isCreateEvent!: boolean;
  @equal('type', 'batch_update') isBatchUpdate!: boolean;

  @computed('type', 'action.type')
  get isHiddenStep() {
    return this.type === 'filter_match_delay' || this.action?.type === 'filter_match_delay_action';
  }

  @computed('type', 'action.type')
  get isWaitUntil() {
    if (this.isSkipped) {
      return false;
    }

    if (this.isHold) {
      return true;
    }

    return this.isDelay && this.action?.isWaitUntil;
  }

  @computed('type')
  get showActionTypeName() {
    if (this.isExit || this.isCreateEvent) {
      return false;
    }

    return true;
  }

  get unversionedActionId() {
    // Action ids can be of the form 12-2, where 12 is the action id and 2 is
    // the version. We only care about the action id part for linking to the
    // action in the VWB
    let id = String((this as SubjectStepModel).belongsTo('action').id());
    return id.split('-')[0];
  }

  get campaignId() {
    let action = (this as SubjectStepModel).belongsTo('action').value();

    if (action == null) {
      return null;
    }

    return (action as ActionModel).belongsTo('campaign').id();
  }

  get customerId() {
    let subject = (this as SubjectStepModel).belongsTo('subject').value();

    if (subject == null) {
      return null;
    }

    return (subject as SubjectModel).belongsTo('customer').id();
  }
}

function computedTypeIn(types: readonly TYPE_STRING_TYPE[]) {
  return computed('type', function () {
    return types.includes(this.type);
  });
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    'subject-step': SubjectStepModel;
  }
}
